import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading, Reveal } from "./primitives";

const SNIPPETS = {
    "Node.js": `const API = "https://mdhapi.com/api";
const TOKEN = process.env.MDH_TOKEN; // Settings → API → Create token
const APP = "app_…";                  // from your app's dashboard

// Point an endpoint at your handler
await fetch(\`\${API}/apps/\${APP}/endpoints\`, {
  method: "POST",
  headers: { Authorization: \`Bearer \${TOKEN}\`,
             "Content-Type": "application/json" },
  body: JSON.stringify({
    url: "https://api.acme.dev/hooks",
    events: ["payment.*"],
  }),
});

// Publish once — MDH signs + delivers everywhere
await fetch(\`\${API}/apps/\${APP}/publish\`, {
  method: "POST",
  headers: { Authorization: \`Bearer \${TOKEN}\`,
             "Content-Type": "application/json" },
  body: JSON.stringify({
    type: "payment.succeeded",
    data: { id: "pay_4f92ka", amount: 4200 },
  }),
});`,
    Python: `import os, requests

API = "https://mdhapi.com/api"
H = {"Authorization": f"Bearer {os.environ['MDH_TOKEN']}"}
APP = "app_…"  # from your app's dashboard

# Point an endpoint at your handler
requests.post(f"{API}/apps/{APP}/endpoints", headers=H, json={
    "url": "https://api.acme.dev/hooks",
    "events": ["payment.*"],
})

# Publish once — MDH signs + delivers everywhere
requests.post(f"{API}/apps/{APP}/publish", headers=H, json={
    "type": "payment.succeeded",
    "data": {"id": "pay_4f92ka", "amount": 4200},
})`,
    cURL: `# Personal token: Settings → API → Create token
export MDH_TOKEN=mdh_pat_…
APP=app_…   # from your app's dashboard

# Point an endpoint at your handler
curl -X POST https://mdhapi.com/api/apps/$APP/endpoints \\
  -H "Authorization: Bearer $MDH_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://api.acme.dev/hooks",
       "events": ["payment.*"]}'

# Publish once — MDH signs + delivers everywhere
curl -X POST https://mdhapi.com/api/apps/$APP/publish \\
  -H "Authorization: Bearer $MDH_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "payment.succeeded",
       "data": {"id": "pay_4f92ka", "amount": 4200}}'`,
    Express: `// Receiving side: verify MDH's signature before trusting
// a delivery. Raw body required — no JSON.parse first.
const crypto = require("crypto");

app.post("/hooks",
  express.raw({ type: "application/json" }),
  (req, res) => {
    const parts = Object.fromEntries(
      req.headers["x-mdh-signature"].split(",")
        .map((kv) => kv.split("=")));
    const expected = crypto
      .createHmac("sha256", process.env.MDH_ENDPOINT_SECRET)
      .update(\`\${parts.t}.\${req.body}\`)
      .digest("hex");
    const a = Buffer.from(expected);
    const b = Buffer.from(parts.v1 || "");
    if (a.length !== b.length || !crypto.timingSafeEqual(a, b))
      return res.sendStatus(401);

    const event = JSON.parse(req.body);
    console.log("verified:", event.type, event.data);
    res.sendStatus(200);
  });`,
};

const STEPS = [
    {
        n: "01",
        title: "Create an app",
        copy: "Pick a template in the dashboard — every app gets its own keys, URLs, and live stream. Grab a personal token from Settings → API.",
    },
    {
        n: "02",
        title: "Register an endpoint",
        copy: "One call binds a URL to an event pattern. MDH provisions the HMAC signing secret instantly — store it, it's shown once.",
    },
    {
        n: "03",
        title: "Publish and forget",
        copy: "Events are durably logged, filtered, signed, and delivered — with the full trail in your command center.",
    },
];

const colorize = (code) =>
    code.split("\n").map((line, i) => {
        let cls = "text-slate-300";
        if (line.trim().startsWith("//") || line.trim().startsWith("#")) cls = "text-slate-600 italic";
        return (
            <div key={i} className={cls}>
                {line
                    .split(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g)
                    .map((part, j) =>
                        /^["']/.test(part) ? (
                            <span key={j} className="text-emerald-300/90">
                                {part}
                            </span>
                        ) : (
                            part
                        ),
                    )}
            </div>
        );
    });

export default function DeveloperWorkflow() {
    const langs = Object.keys(SNIPPETS);
    const [lang, setLang] = useState("Node.js");
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(SNIPPETS[lang]);
            setCopied(true);
            toast.success("Copied to clipboard", {
                description: `${lang} quickstart — paste it into your project.`,
            });
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Copy failed", { description: "Select the code manually." });
        }
    };

    return (
        <section
            id="developers"
            data-testid="developer-workflow-section"
            className="relative border-y border-slate-800/70 bg-[#0D1320]/60 py-24 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <SectionHeading
                    testid="developers"
                    eyebrow="Developer Workflow"
                    title="Production webhooks in three lines."
                    copy="No queues to provision, no workers to babysit. Install, register, publish — MDH owns everything after the await."
                />

                <div className="mt-12 grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
                    <ol className="space-y-2">
                        {STEPS.map((s, i) => (
                            <Reveal
                                key={s.n}
                                delay={i * 0.1}
                                className="group flex gap-5 rounded-xl border border-transparent p-5 transition-colors hover:border-slate-800 hover:bg-slate-900/40"
                                data-testid={`workflow-step-${s.n}`}
                            >
                                <span className="font-display text-2xl font-black text-slate-700 transition-colors group-hover:text-cyan-400">
                                    {s.n}
                                </span>
                                <div>
                                    <h3 className="font-display text-base font-bold text-slate-100">
                                        {s.title}
                                    </h3>
                                    <p className="mt-1.5 text-xs leading-relaxed text-slate-400 sm:text-sm">
                                        {s.copy}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </ol>

                    <Reveal delay={0.15}>
                        <div className="glass-panel overflow-hidden rounded-xl shadow-[0_30px_60px_-30px_rgba(0,0,0,0.8)]">
                            <div className="flex items-center justify-between border-b border-slate-800/80 px-3">
                                <div className="flex gap-0.5 overflow-x-auto">
                                    {langs.map((l) => (
                                        <button
                                            key={l}
                                            data-testid={`code-tab-${l.toLowerCase().replace(/[^a-z]/g, "")}`}
                                            onClick={() => setLang(l)}
                                            className={`whitespace-nowrap px-3.5 py-2.5 font-mono text-[11px] transition-colors ${
                                                lang === l
                                                    ? "border-b border-cyan-400 text-cyan-300"
                                                    : "text-slate-500 hover:text-slate-300"
                                            }`}
                                        >
                                            {l}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    data-testid="code-copy-button"
                                    onClick={copy}
                                    className="ml-2 flex shrink-0 items-center gap-1.5 rounded border border-slate-700/80 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                                >
                                    {copied ? (
                                        <Check className="h-3 w-3 text-emerald-400" />
                                    ) : (
                                        <Copy className="h-3 w-3" />
                                    )}
                                    {copied ? "copied" : "copy"}
                                </button>
                            </div>
                            <pre
                                data-testid="code-block"
                                className="max-h-[420px] overflow-auto p-5 font-mono text-[11px] leading-relaxed sm:text-xs"
                            >
                                {colorize(SNIPPETS[lang])}
                            </pre>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
